import React, { useEffect, useRef, useState } from "react";
import {
  Button, Card, CardActionArea, CardActions, CircularProgress, Dialog,
  DialogActions, DialogContent, DialogTitle, Grid, Typography,
  useMediaQuery, useTheme,
} from "@material-ui/core";
import DescriptionIcon from "@material-ui/icons/Description";
import ImageIcon from "@material-ui/icons/Image";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import { useTranslation } from "react-i18next";
import { getToken } from "../Utils/Common";
import EnvSettings from "../Utils/EnvSettings";
import MediaPreviewImage from "./MediaPreviewImage";

const PREVIEW_CACHE_TTL = 5 * 60 * 1000;
const PREVIEW_CACHE_LIMIT = 20;

async function getCachedPreview(cache, context, filename, signal) {
  const now = Date.now();
  for (const [key, entry] of cache) {
    if (entry.expiresAt <= now) cache.delete(key);
  }
  const cached = cache.get(filename);
  if (cached) {
    // Keep the most recently used URLs so the browser can reuse downloaded files.
    cache.delete(filename);
    cache.set(filename, cached);
    return cached.result;
  }
  const result = await getMediaUrl(context, filename, false, signal);
  if (!signal.aborted) {
    cache.set(filename, { result, expiresAt: now + PREVIEW_CACHE_TTL });
    while (cache.size > PREVIEW_CACHE_LIMIT) cache.delete(cache.keys().next().value);
  }
  return result;
}

async function getMediaUrl(context, filename, thumbnail, signal) {
  const response = await fetch(EnvSettings.server + "/get_media_preview", {
    method: "POST", headers: { "Content-Type": "application/json" }, signal,
    body: JSON.stringify({ ...context, token: getToken(), filename, thumbnail }),
  });
  const result = await response.json();
  if (!response.ok || result.error || !result.url) throw new Error("previewFailed");
  return result;
}

function FileIcon({ contentType }) {
  const Icon = contentType === "application/pdf" ? PictureAsPdfIcon :
    contentType.startsWith("image/") ? ImageIcon : DescriptionIcon;
  return <Icon style={{ fontSize: 56, color: "#777" }} />;
}

function Thumbnail({ file, contextKey }) {
  const tile = useRef();
  const [visible, setVisible] = useState(false);
  const [url, setUrl] = useState();
  const [failed, setFailed] = useState(false);
  const { t } = useTranslation();
  useEffect(() => {
    if (!file.thumbnail) return;
    if (!window.IntersectionObserver) { setVisible(true); return; }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: "150px" });
    observer.observe(tile.current);
    return () => observer.disconnect();
  }, [file.thumbnail]);
  useEffect(() => {
    if (!visible || !file.thumbnail) return;
    const controller = new AbortController();
    setUrl(undefined);
    setFailed(false);
    getMediaUrl(JSON.parse(contextKey), file.filename, true, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setUrl(result.url); })
      .catch(() => { if (!controller.signal.aborted) setFailed(true); });
    return () => controller.abort();
  }, [visible, file.filename, file.thumbnail, contextKey]);
  return (
    <div ref={tile} style={{ height: 160, display: "flex", alignItems: "center",
      justifyContent: "center", background: "#f5f5f5" }}>
      {url && !failed ? <img src={url} alt={file.filename} loading="lazy"
        onError={() => setFailed(true)} style={{ width: "100%", height: "100%", objectFit: "contain" }} /> :
        file.thumbnail && visible && !failed ? <CircularProgress size={24} aria-label={t("mediaPreview.loading")} /> :
          <FileIcon contentType={file.contentType} />}
    </div>
  );
}

export default function MediaGallery({ attachments = [], resource, deviceid, onDelete }) {
  const { t } = useTranslation();
  const fullScreen = useMediaQuery(useTheme().breakpoints.down("sm"));
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState();
  const [error, setError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [downloadError, setDownloadError] = useState(false);
  const downloadController = useRef();
  const previewCache = useRef(new Map());
  const token = getToken();
  // Ignore unrelated resource refresh fields when deciding which entity is selected.
  const contextKey = JSON.stringify({ resource: resource ? {
    type: resource.type, number: resource.number,
  } : undefined, deviceid });
  const file = attachments.find((attachment) => attachment.filename === selected);
  const index = attachments.findIndex((attachment) => attachment.filename === selected);
  const attachmentsKey = JSON.stringify(attachments.map((attachment) => [
    attachment.filename, attachment.version, attachment.size, attachment.thumbnail,
  ]));
  useEffect(() => {
    setSelected(null);
    setDownloadError(false);
    return () => { if (downloadController.current) downloadController.current.abort(); };
  }, [contextKey, token]);
  useEffect(() => {
    // Refreshes/overwrites/deletions and account/entity changes invalidate old URLs.
    previewCache.current.clear();
  }, [contextKey, token, attachmentsKey]);
  useEffect(() => {
    setPreview(undefined);
    setError(false);
    setZoom(1);
    setRotation(0);
    if (!selected) return;
    const controller = new AbortController();
    getCachedPreview(previewCache.current, JSON.parse(contextKey), selected, controller.signal)
      .then((result) => { if (!controller.signal.aborted) setPreview(result); })
      .catch(() => { if (!controller.signal.aborted) setError(true); });
    return () => controller.abort();
  }, [selected, contextKey, retry, token, attachmentsKey]);

  const download = async (filename) => {
    if (downloadController.current) downloadController.current.abort();
    const controller = new AbortController();
    downloadController.current = controller;
    setDownloadError(false);
    try {
      const result = await getMediaUrl(JSON.parse(contextKey), filename, false, controller.signal);
      if (controller.signal.aborted) return;
      const link = document.createElement("a");
      link.href = result.downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) { if (!controller.signal.aborted) setDownloadError(true); }
  };
  const image = preview && /^(image\/(jpeg|png|gif|webp|avif|bmp))$/.test(preview.contentType);
  return (
    <>
      {downloadError && <Typography color="error" role="alert">{t("mediaPreview.downloadFailed")}</Typography>}
      <Grid container spacing={2}>
        {attachments.map((attachment) => (
          <Grid item xs={6} sm={4} md={3} key={attachment.filename}>
            <Card variant="outlined">
              <CardActionArea onClick={() => setSelected(attachment.filename)}
                aria-label={t("mediaPreview.open", { filename: attachment.filename })}>
                <Thumbnail file={attachment} contextKey={contextKey} />
                <Typography variant="body2" title={attachment.filename}
                  style={{ padding: 8, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>
                  {attachment.filename}
                </Typography>
              </CardActionArea>
              <CardActions style={{ flexWrap: "wrap" }}>
                <Button size="small" onClick={() => download(attachment.filename)}>{t("mediaPreview.download")}</Button>
                {onDelete && <Button size="small" color="secondary" onClick={() => onDelete(attachment.filename)}>{t("delete")}</Button>}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Dialog open={!!file} onClose={() => setSelected(null)} fullWidth maxWidth="lg"
        fullScreen={fullScreen} aria-labelledby="media-preview-title">
        <DialogTitle id="media-preview-title" style={{ overflowWrap: "anywhere" }}>{selected}</DialogTitle>
        <DialogContent style={{ minHeight: "60vh", overflow: "auto", textAlign: "center" }}>
          {error ? <Typography color="error" role="alert">{t("mediaPreview.failed")}</Typography> :
            !preview ? <CircularProgress aria-label={t("mediaPreview.loading")} /> :
              image ? <MediaPreviewImage key={preview.url} src={preview.url} alt={selected}
                zoom={zoom} rotation={rotation} onError={() => {
                previewCache.current.delete(selected);
                setError(true);
              }} /> :
                preview.contentType === "application/pdf" ?
                  <iframe src={preview.url} title={selected} style={{ width: "100%", height: "65vh", border: 0 }} /> :
                  <Typography>{t("mediaPreview.unsupported")}</Typography>}
        </DialogContent>
        <DialogActions style={{ flexWrap: "wrap" }}>
          <Button disabled={index <= 0} onClick={() => setSelected(attachments[index - 1].filename)}>{t("mediaPreview.previous")}</Button>
          <Button disabled={index < 0 || index >= attachments.length - 1}
            onClick={() => setSelected(attachments[index + 1].filename)}>{t("mediaPreview.next")}</Button>
          {image && !error && <>
            <Button disabled={zoom <= 1} onClick={() => setZoom(Math.max(1, zoom - 0.5))}>{t("mediaPreview.zoomOut")}</Button>
            <Button disabled={zoom >= 4} onClick={() => setZoom(zoom + 0.5)}>{t("mediaPreview.zoomIn")}</Button>
            <Button onClick={() => setRotation((rotation + 90) % 360)}>{t("mediaPreview.rotate")}</Button>
          </>}
          {error && <Button onClick={() => {
            previewCache.current.delete(selected);
            setRetry(retry + 1);
          }}>{t("mediaPreview.retry")}</Button>}
          <Button onClick={() => download(selected)}>{t("mediaPreview.download")}</Button>
          <Button onClick={() => setSelected(null)}>{t("mediaPreview.close")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
